import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, Observable, catchError, throwError } from 'rxjs';
import { AuthService } from '../service/auth.service';
import { ToastService } from '../service/toast.service';

/**
 * Adds the Bearer token to outgoing requests, redirects to login when no
 * token is present, and on 401 clears the session and bounces to /auth/login
 * with a `returnUrl` so the user lands back where they came from.
 *
 * Loader concerns live in `LoaderInterceptor` (single-responsibility per
 * implementation-guidelines §12.1).
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly ignoredRoutes: string[] = [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/verify-reset-otp',
    '/auth/reset-password',
  ];

  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isAuthRoute = this.ignoredRoutes.some((route) => req.url.includes(route));
    if (isAuthRoute) {
      return next.handle(req);
    }

    const token = this.authService.getAuthToken()?.access_token;
    if (!token) {
      this.router.navigate(['/auth/login']);
      return EMPTY;
    }

    const cloned = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`),
    });

    return next.handle(cloned).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.handleUnauthorized();
        }
        return throwError(() => err);
      }),
    );
  }

  private handleUnauthorized(): void {
    const currentUrl = this.router.url;
    const returnUrl = currentUrl && !currentUrl.startsWith('/auth') && currentUrl !== '/' ? currentUrl : undefined;
    this.authService.clearSession(returnUrl);
    this.toast.showWarn('Session expired', 'Your session has expired. Please log in again.');
  }
}
